import * as RadixPopover from '@radix-ui/react-popover';
import { memo, useCallback, useRef } from 'react';
import { useIsomorphicLayoutEffect } from 'react-use';

import { usePortalStore } from '@air/primitive-portal';
import { Spinner } from '@air/primitive-spinner';

import { WorkspaceAgentMentionBrandKitFilterChip } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/mentions/WorkspaceAgentMentionBrandKitFilterChip';
import { WorkspaceAgentSelectAssetBoardFilterChip } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/select-asset/WorkspaceAgentSelectAssetBoardFilterChip';
import { WorkspaceAgentSelectAssetBoardPickerList } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/select-asset/WorkspaceAgentSelectAssetBoardPickerList';
import { WorkspaceAgentSelectAssetBoardPickerSearchField } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/select-asset/WorkspaceAgentSelectAssetBoardPickerSearchField';
import { WorkspaceAgentSelectAssetClearButton } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/select-asset/WorkspaceAgentSelectAssetClearButton';
import { WorkspaceAgentSelectAssetList } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/select-asset/WorkspaceAgentSelectAssetList';
import { WorkspaceAgentSelectAssetSearchField } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/select-asset/WorkspaceAgentSelectAssetSearchField';
import {
  usePublishWorkspaceAgentSelectAssetResults,
  useWorkspaceAgentSelectAssetStore,
} from '~/components/v2/components/workspace/workspace-agent-panel/design-system/select-asset/WorkspaceAgentSelectAssetStoreContext';
import { WorkspaceAgentSelectAssetTrigger } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/select-asset/WorkspaceAgentSelectAssetTrigger';
import {
  type WorkspaceAgentSelectAssetResults,
  type WorkspaceAgentSelectAssetValue,
} from '~/components/v2/components/workspace/workspace-agent-panel/design-system/select-asset/workspaceAgentSelectAssetTypes';
import { type WorkspaceAgentBrandKitOption } from '~/components/v2/components/workspace/workspace-agent-panel/mentions/types';
import { WORKSPACE_AGENT_MENTION_SOURCES_BY_KEY } from '~/components/v2/components/workspace/workspace-agent-panel/mentions/workspaceAgentMentionSources';

type WorkspaceAgentSelectAssetPointerDownOutsideEvent = Parameters<
  NonNullable<RadixPopover.PopoverContentProps['onPointerDownOutside']>
>[0];

const SELECT_ASSET_BOARD_PICKER_LAYER = 'board-picker';

const isEventTargetInsideSelectAssetLayer = (target: EventTarget | null, layer: string) => {
  if (!(target instanceof Element)) {
    return false;
  }

  return Boolean(target.closest(`[data-select-asset-layer="${layer}"]`));
};

export type WorkspaceAgentSelectAssetDropdownProps = {
  assetResults: WorkspaceAgentSelectAssetResults;
  boardResults: WorkspaceAgentSelectAssetResults;
  brandKits: readonly WorkspaceAgentBrandKitOption[];
  'data-testid'?: string;
  id?: string;
  onSelectBrandKit: (brandKitId: string) => void;
  onValueChange?: (value: WorkspaceAgentSelectAssetValue | null) => void;
  selectedBrandKitId: string | null;
};

export const WorkspaceAgentSelectAssetDropdown = memo(
  ({
    assetResults,
    boardResults,
    brandKits,
    'data-testid': testId,
    id,
    onSelectBrandKit,
    onValueChange,
    selectedBrandKitId,
  }: WorkspaceAgentSelectAssetDropdownProps) => {
    usePublishWorkspaceAgentSelectAssetResults(assetResults, boardResults, onValueChange);
    const isOpen = useWorkspaceAgentSelectAssetStore((state) => state.isOpen);
    const source = useWorkspaceAgentSelectAssetStore((state) => state.source);
    const isBoardPickerOpen = useWorkspaceAgentSelectAssetStore((state) => state.isBoardPickerOpen);
    const setBoardPickerOpen = useWorkspaceAgentSelectAssetStore((state) => state.setBoardPickerOpen);
    const setOpen = useWorkspaceAgentSelectAssetStore((state) => state.setOpen);
    const chooseSource = useWorkspaceAgentSelectAssetStore((state) => state.chooseSource);
    const showAssetFetching = useWorkspaceAgentSelectAssetStore(
      (state) => state.assetResults.items.length > 0 && (state.assetResults.isStale || state.assetResults.isFetching),
    );
    const listRef = useRef<HTMLDivElement>(null);
    const boardPickerListRef = useRef<HTMLDivElement>(null);
    const searchRef = useRef<HTMLInputElement | null>(null);
    const boardPickerSearchRef = useRef<HTMLInputElement | null>(null);
    const portal = usePortalStore((state) => state.portal);

    const handleBrandKitSourceChange = useCallback(() => {
      chooseSource('brand-kit');
    }, [chooseSource]);

    const handlePointerDownOutside = useCallback((event: WorkspaceAgentSelectAssetPointerDownOutsideEvent) => {
      const target = event.detail.originalEvent.target;

      if (
        (target instanceof Node && searchRef.current?.contains(target)) ||
        isEventTargetInsideSelectAssetLayer(target, SELECT_ASSET_BOARD_PICKER_LAYER) ||
        (target instanceof Element && target.closest('[data-radix-dropdown-menu-content]'))
      ) {
        event.preventDefault();
      }
    }, []);

    const showAssetSearch = source !== 'brand-kit';

    const focusAssetList = useCallback(() => {
      listRef.current?.querySelector<HTMLElement>('[role="listbox"]')?.focus();
    }, []);

    const handleRefocusKeyboardHost = useCallback(() => {
      searchRef.current?.focus();

      if (document.activeElement === searchRef.current) {
        return;
      }

      focusAssetList();
    }, [focusAssetList]);

    const handleOpenAutoFocus = useCallback(
      (event: Event) => {
        event.preventDefault();
        handleRefocusKeyboardHost();
      },
      [handleRefocusKeyboardHost],
    );

    const handleBrandKitCaretShiftTab = useCallback(() => {
      setOpen(false);
    }, [setOpen]);

    const handleBoardPickerOpenAutoFocus = useCallback((event: Event) => {
      event.preventDefault();
      boardPickerSearchRef.current?.focus();
    }, []);

    const handleBoardPickerCloseAutoFocus = useCallback(
      (event: Event) => {
        event.preventDefault();
        handleRefocusKeyboardHost();
      },
      [handleRefocusKeyboardHost],
    );

    const wasBoardPickerOpenRef = useRef(isBoardPickerOpen);

    useIsomorphicLayoutEffect(() => {
      const wasBoardPickerOpen = wasBoardPickerOpenRef.current;
      wasBoardPickerOpenRef.current = isBoardPickerOpen;

      if (!wasBoardPickerOpen || isBoardPickerOpen || !isOpen) {
        return;
      }

      handleRefocusKeyboardHost();
      const frame = window.requestAnimationFrame(handleRefocusKeyboardHost);

      return () => {
        window.cancelAnimationFrame(frame);
      };
    }, [handleRefocusKeyboardHost, isBoardPickerOpen, isOpen]);

    const previousSourceRef = useRef(source);

    useIsomorphicLayoutEffect(() => {
      const previousSource = previousSourceRef.current;
      previousSourceRef.current = source;

      if (!isOpen || isBoardPickerOpen || previousSource === source) {
        return;
      }

      handleRefocusKeyboardHost();
      const frame = window.requestAnimationFrame(handleRefocusKeyboardHost);

      return () => {
        window.cancelAnimationFrame(frame);
      };
    }, [handleRefocusKeyboardHost, isBoardPickerOpen, isOpen, source]);

    return (
      <RadixPopover.Root onOpenChange={setOpen} open={isOpen}>
        <div className="relative flex w-full min-w-0">
          <RadixPopover.Trigger asChild>
            <WorkspaceAgentSelectAssetTrigger data-testid={testId} id={id} />
          </RadixPopover.Trigger>
          <WorkspaceAgentSelectAssetClearButton />
        </div>
        <RadixPopover.Portal container={portal ?? undefined}>
          <RadixPopover.Content
            align="start"
            className="z-portal flex h-80 w-[var(--radix-popover-trigger-width)] flex-col overflow-hidden rounded-12 border border-gray-4 bg-gray-1 p-2 shadow-popover outline-none"
            collisionPadding={8}
            data-preserves-selection
            data-testid="WORKSPACE_AGENT_SELECT_ASSET_DROPDOWN"
            onOpenAutoFocus={handleOpenAutoFocus}
            onPointerDownOutside={handlePointerDownOutside}
            side="bottom"
            sideOffset={4}
          >
            <div className="px-1 pb-1 text-12 text-gray-10" data-testid="WORKSPACE_AGENT_SELECT_ASSET_TITLE">
              Insert reference
            </div>
            <fieldset aria-label="Asset source" className="flex min-w-0 flex-nowrap items-center gap-1 pb-2">
              {brandKits.length > 0 ? (
                <WorkspaceAgentMentionBrandKitFilterChip
                  brandKits={brandKits}
                  isActive={source === 'brand-kit' && !isBoardPickerOpen}
                  label={WORKSPACE_AGENT_MENTION_SOURCES_BY_KEY['brand-kit'].label}
                  onRefocusEditor={handleRefocusKeyboardHost}
                  onSelect={handleBrandKitSourceChange}
                  onSelectBrandKit={onSelectBrandKit}
                  onShiftTab={handleBrandKitCaretShiftTab}
                  selectedBrandKitId={selectedBrandKitId}
                  sourceKey="brand-kit"
                />
              ) : null}
              <RadixPopover.Root modal={false} onOpenChange={setBoardPickerOpen} open={isBoardPickerOpen}>
                <RadixPopover.Anchor asChild>
                  <WorkspaceAgentSelectAssetBoardFilterChip />
                </RadixPopover.Anchor>
                <RadixPopover.Portal container={portal ?? undefined}>
                  <RadixPopover.Content
                    align="start"
                    className="z-portal flex h-[min(20rem,var(--radix-popover-content-available-height))] min-h-0 w-80 flex-col gap-1 overflow-hidden rounded-12 border border-gray-4 bg-gray-1 p-2 shadow-popover outline-none"
                    collisionPadding={8}
                    data-preserves-selection
                    data-select-asset-layer={SELECT_ASSET_BOARD_PICKER_LAYER}
                    data-testid="WORKSPACE_AGENT_SELECT_ASSET_BOARD_PICKER"
                    onCloseAutoFocus={handleBoardPickerCloseAutoFocus}
                    onOpenAutoFocus={handleBoardPickerOpenAutoFocus}
                    side="bottom"
                    sideOffset={8}
                  >
                    <div className="shrink-0 px-1 text-12 text-gray-10">Choose board</div>
                    <WorkspaceAgentSelectAssetBoardPickerSearchField inputRef={boardPickerSearchRef} />
                    <div className="flex min-h-0 min-w-0 flex-1 flex-col pt-1">
                      <WorkspaceAgentSelectAssetBoardPickerList listRef={boardPickerListRef} />
                    </div>
                  </RadixPopover.Content>
                </RadixPopover.Portal>
              </RadixPopover.Root>
              {showAssetFetching ? <Spinner aria-hidden className="ml-auto size-4 shrink-0 text-gray-9" /> : null}
            </fieldset>
            <div className="flex min-h-0 flex-1 flex-col gap-2">
              {showAssetSearch ? <WorkspaceAgentSelectAssetSearchField inputRef={searchRef} /> : null}
              <WorkspaceAgentSelectAssetList listRef={listRef} />
            </div>
          </RadixPopover.Content>
        </RadixPopover.Portal>
      </RadixPopover.Root>
    );
  },
);

WorkspaceAgentSelectAssetDropdown.displayName = 'WorkspaceAgentSelectAssetDropdown';
